import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { EventBranding } from '@/types';

interface ThemeContextType {
  branding: EventBranding | null;
  setBranding: (branding: EventBranding | null) => void;
  applyBranding: (branding: EventBranding) => void;
  resetBranding: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Helper to convert hex to RGB values
function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    return `${parseInt(result[1], 16)} ${parseInt(result[2], 16)} ${parseInt(result[3], 16)}`;
  }
  return '';
}

// Generate color palette from a base color
function generatePalette(baseHex: string): Record<string, string> {
  const base = hexToRgb(baseHex);
  if (!base) return {};

  // Parse base RGB
  const [r, g, b] = base.split(' ').map(Number);

  // Generate lighter and darker variants
  const lighten = (amount: number) => {
    const lr = Math.min(255, Math.round(r + (255 - r) * amount));
    const lg = Math.min(255, Math.round(g + (255 - g) * amount));
    const lb = Math.min(255, Math.round(b + (255 - b) * amount));
    return `${lr} ${lg} ${lb}`;
  };

  const darken = (amount: number) => {
    const dr = Math.round(r * (1 - amount));
    const dg = Math.round(g * (1 - amount));
    const db = Math.round(b * (1 - amount));
    return `${dr} ${dg} ${db}`;
  };

  return {
    50: lighten(0.95),
    100: lighten(0.9),
    200: lighten(0.7),
    300: lighten(0.5),
    400: lighten(0.25),
    500: base,
    600: darken(0.1),
    700: darken(0.25),
    800: darken(0.4),
    900: darken(0.55),
  };
}

// Default theme values
const DEFAULT_PRIMARY = '#a855f7'; // Purple
const DEFAULT_SECONDARY = '#64748b'; // Slate
const DEFAULT_ACCENT = '#f59e0b'; // Amber

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<EventBranding | null>(null);

  const applyBranding = (newBranding: EventBranding) => {
    const root = document.documentElement;

    // Apply primary color
    if (newBranding.primary_color) {
      const palette = generatePalette(newBranding.primary_color);
      Object.entries(palette).forEach(([shade, value]) => {
        root.style.setProperty(`--color-primary-${shade}`, value);
      });
    }

    // Apply secondary color
    if (newBranding.secondary_color) {
      const secRgb = hexToRgb(newBranding.secondary_color);
      if (secRgb) {
        root.style.setProperty('--color-secondary-500', secRgb);
        // Generate darker variants
        const [r, g, b] = secRgb.split(' ').map(Number);
        root.style.setProperty('--color-secondary-600', `${Math.round(r * 0.9)} ${Math.round(g * 0.9)} ${Math.round(b * 0.9)}`);
        root.style.setProperty('--color-secondary-700', `${Math.round(r * 0.75)} ${Math.round(g * 0.75)} ${Math.round(b * 0.75)}`);
      }
    }

    // Apply accent color
    if (newBranding.accent_color) {
      const accRgb = hexToRgb(newBranding.accent_color);
      if (accRgb) {
        const [r, g, b] = accRgb.split(' ').map(Number);
        root.style.setProperty('--color-accent-400', `${Math.min(255, r + 20)} ${Math.min(255, g + 20)} ${Math.min(255, b + 20)}`);
        root.style.setProperty('--color-accent-500', accRgb);
        root.style.setProperty('--color-accent-600', `${Math.round(r * 0.85)} ${Math.round(g * 0.85)} ${Math.round(b * 0.85)}`);
      }
    }

    // Apply font family
    if (newBranding.font_family) {
      root.style.setProperty('--font-display', newBranding.font_family);
    }

    // Apply custom CSS
    if (newBranding.custom_css) {
      let customStyle = document.getElementById('event-custom-css');
      if (!customStyle) {
        customStyle = document.createElement('style');
        customStyle.id = 'event-custom-css';
        document.head.appendChild(customStyle);
      }
      customStyle.textContent = newBranding.custom_css;
    }

    setBranding(newBranding);
  };

  const resetBranding = () => {
    const root = document.documentElement;

    // Reset primary
    const defaultPrimary = generatePalette(DEFAULT_PRIMARY);
    Object.entries(defaultPrimary).forEach(([shade, value]) => {
      root.style.setProperty(`--color-primary-${shade}`, value);
    });

    // Reset secondary
    const secRgb = hexToRgb(DEFAULT_SECONDARY);
    if (secRgb) {
      root.style.setProperty('--color-secondary-500', secRgb);
      const [r, g, b] = secRgb.split(' ').map(Number);
      root.style.setProperty('--color-secondary-600', `${Math.round(r * 0.9)} ${Math.round(g * 0.9)} ${Math.round(b * 0.9)}`);
      root.style.setProperty('--color-secondary-700', `${Math.round(r * 0.75)} ${Math.round(g * 0.75)} ${Math.round(b * 0.75)}`);
    }

    // Reset accent
    const accRgb = hexToRgb(DEFAULT_ACCENT);
    if (accRgb) {
      const [r, g, b] = accRgb.split(' ').map(Number);
      root.style.setProperty('--color-accent-400', `${Math.min(255, r + 20)} ${Math.min(255, g + 20)} ${Math.min(255, b + 20)}`);
      root.style.setProperty('--color-accent-500', accRgb);
      root.style.setProperty('--color-accent-600', `${Math.round(r * 0.85)} ${Math.round(g * 0.85)} ${Math.round(b * 0.85)}`);
    }

    // Reset fonts
    root.style.setProperty('--font-display', "'Playfair Display', serif");
    root.style.setProperty('--font-body', "'Inter', sans-serif");

    // Remove custom CSS
    const customStyle = document.getElementById('event-custom-css');
    if (customStyle) {
      customStyle.remove();
    }

    setBranding(null);
  };

  // Initialize default theme on mount
  useEffect(() => {
    resetBranding();
  }, []);

  return (
    <ThemeContext.Provider value={{ branding, setBranding, applyBranding, resetBranding }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
